import React, { useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import { Upload, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { MAX_FILE_SIZE, SUPPORTED_VIDEO_TYPES } from '../../config/constants';

interface VideoUploaderProps {
  onUpload: (file: File) => void;
}

export const VideoUploader: React.FC<VideoUploaderProps> = ({ onUpload }) => {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateFile = (file: File): string | null => {
    if (!SUPPORTED_VIDEO_TYPES.includes(file.type)) {
      return 'Please upload a supported video format (MP4, MOV, or WebM)';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File size must be less than 64MB';
    }
    return null;
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const error = validateFile(file);
      if (error) {
        setError(error);
      } else {
        setError(null);
        onUpload(file);
      }
    }
  }, [onUpload]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const error = validateFile(file);
      if (error) {
        setError(error);
      } else {
        setError(null);
        onUpload(file);
      }
    }
  };

  return (
    <div className="mt-6">
      <div
        className={`relative border-2 border-dashed rounded-xl p-8 text-center ${
          dragActive
            ? 'border-red-600 bg-red-50'
            : 'border-gray-300 hover:border-red-600'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept="video/*"
          onChange={handleChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
        
        <div className="space-y-4">
          <div className="flex justify-center">
            <Upload className={`w-12 h-12 ${dragActive ? 'text-red-600' : 'text-gray-400'}`} />
          </div>
          <div>
            <p className="text-lg font-medium text-gray-900">
              Drag and drop your video here
            </p>
            <p className="text-sm text-gray-500">
              or click to browse (MP4, MOV up to 64MB)
            </p>
          </div>
        </div>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 p-4 bg-red-50 rounded-lg flex items-start"
        >
          <div className="flex-shrink-0">
            <X className="h-5 w-5 text-red-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </motion.div>
      )}
    </div>
  );
};